// Pure logic for the Create Bundle form, extracted for testability.

// Client-side ids keep React keys stable when accounts/devices are removed
// from the middle of the list (index keys shift row state onto the wrong row).
export function emptyDevice() {
  // connection: 'online' (default: only devices connected to Emporia Cloud now) |
  // 'any' | 'offline' — matches the API's BundleDeviceSpec.connection.
  // is_virtual: '' (any, the default) | 'true' (virtual only) | 'false' (physical
  // only) — the select's string form of the API's tri-state is_virtual.
  return { id: crypto.randomUUID(), category: 'VUE', model: null, count: 1, capabilities: [], connection: 'online', is_virtual: '' }
}

export function emptyAccount() {
  return {
    id: crypto.randomUUID(),
    environment: 'DEV3',
    account_type: 'CUSTOMER',
    federations: ['emporia'],
    has_public_charger: false,
    // Customer-only: put the claimed account in the PostHog "QA internal ring".
    internal_ring: false,
    // Empty: devices are optional in a bundle, so a new account block starts with
    // none rather than a pre-filled row the caller has to notice and remove.
    devices: [],
  }
}

export function computeUnmet(accounts) {
  const unmet = []
  if (accounts.length === 0) unmet.push('Add at least one account')
  accounts.forEach((a, i) => {
    // Devices are optional — reserving just an account is a legitimate bundle.
    // Federations pick among customer accounts by identity provider. Partner
    // accounts live in a different pool and have none, so the API rejects any.
    if (a.account_type !== 'PARTNER' && a.federations.length === 0) {
      unmet.push(`Account ${i + 1}: select at least one federation`)
    }
  })
  return unmet
}

// Device rows -> the API's BundleDeviceSpec list. Shared by both bundle kinds so
// an inventory bundle and an own-account bundle send identical device specs.
function buildDeviceSpecs(devices) {
  return devices
    .filter(d => d.count > 0)
    .map(d => ({
      category: d.category,
      // model is sent only when chosen; omitting it reserves any device in the category.
      ...(d.model ? { model: d.model } : {}),
      count: d.count,
      ...(d.capabilities?.length ? { capabilities: d.capabilities } : {}),
      connection: d.connection ?? 'online',
      // Sent only when narrowed; omitting it claims any free device, virtual or physical.
      ...(d.is_virtual ? { is_virtual: d.is_virtual === 'true' } : {}),
    }))
}

export function buildBundlePayload(accounts) {
  return {
    accounts: accounts.map(a => ({
      environment: a.environment,
      account_type: a.account_type,
      // Omitted entirely for a partner account — the API 422s on any federation.
      federations: a.account_type === 'PARTNER' ? [] : a.federations,
      // Partner-only: narrows the claim to a partner login that has one.
      has_public_charger:
        a.account_type === 'PARTNER' ? !!a.has_public_charger : false,
      // Customer-only: the API 422s on a partner account that asks for the ring.
      internal_ring:
        a.account_type === 'PARTNER' ? false : !!a.internal_ring,
      devices: buildDeviceSpecs(a.devices),
    })),
  }
}

// ── Own-account ("external") bundle: devices onto an email the inventory does
// not track, via POST /bundle/external. One email per bundle; the API verifies it
// exists in Emporia Cloud for the chosen environment and account type, and
// reserves only the devices (there is no inventory account to reserve).

export function emptyExternal(email = '') {
  return {
    email,
    environment: 'DEV3',
    account_type: 'CUSTOMER',
    // Starts with one row: unlike an inventory bundle, an external bundle with no
    // devices is rejected by the API (there would be nothing to reserve).
    devices: [emptyDevice()],
  }
}

// Loose shape check only — the API validates the address and then confirms the
// account actually exists in Emporia Cloud.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function computeUnmetExternal(ext) {
  const unmet = []
  if (!EMAIL_RE.test(ext.email.trim())) unmet.push('Enter the email of your Emporia account')
  if (!ext.devices.some(d => d.count > 0)) unmet.push('Add at least one device')
  return unmet
}

export function buildExternalPayload(ext) {
  return {
    email: ext.email.trim(),
    environment: ext.environment,
    account_type: ext.account_type,
    devices: buildDeviceSpecs(ext.devices),
  }
}
